/**
 * Passive UX signals: core (Doug LOCK 2026-10-03).
 *
 * Doug does not want the UI interrupted, so there is no "Works / Needs work"
 * prompt any more. Instead the app watches for the cheap signals below and
 * hands them to a sink, which posts them through the existing `app-feedback`
 * edge function (type 'batch') into `public.app_events`. A rollup (Librarian
 * proposal, see docs/architecture/PASSIVE_UX_SIGNALS.md) groups them per Brain
 * screen node and routes hot screens to the node's owner seat through the
 * existing page_verdict -> tasks path.
 *
 *   signal            app_events.event_type  feature                    duration_ms
 *   JS error / crash  error                  js_error | crash           -
 *   slow load         slow_load              -                          screen entered -> first data
 *   abandoned flow    abandon                <flow> (e.g. form)         screen entered -> left
 *   rage taps         tap_rage               <target> | area            -
 *   time to complete  feature_use            flow_complete              flow start -> primary action
 *
 * Only event types already allowed by app_events_event_type_check are used
 * (time-to-complete rides on feature_use), so no schema change is needed to emit.
 *
 * Cheap and private by construction:
 *   - signed-in only: nothing is emitted while isSignedIn() is false;
 *   - throttled: at most one signal per (type, screen, feature) per throttleMs,
 *     and at most maxPerSession signals per app session;
 *   - sampled: time-to-complete is sampled (sampleRates), everything else is rare;
 *   - no PII: screens are route patterns (ids and tokens are replaced by ':id'),
 *     error text is scrubbed of emails, urls, phone/long numbers, uuids and tokens,
 *     tap targets are a test id or tag name, never visible text.
 *
 * This file has no imports so every app (React Native, Vite, Next.js) and its
 * node:test / jest suites can use it as is. Each app keeps an identical copy.
 */

export const UX_SIGNAL_VERSION = 'passive_v1';

export const DEFAULTS = Object.freeze({
  slowLoadMs: 2000,
  throttleMs: 60_000,
  maxPerSession: 80,
  rageTaps: 4,
  rageWindowMs: 1500,
  rageRadiusPx: 30,
  minAbandonDwellMs: 1500,
  sampleRates: Object.freeze({ error: 1, slow_load: 1, abandon: 1, tap_rage: 1, flow_complete: 0.5 }),
});

const MAX_TEXT = 200;
const MAX_KEY = 120;

const UUID_RE = /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/gi;
const EMAIL_RE = /[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}/gi;
const URL_RE = /\bhttps?:\/\/[^\s'"<>)]+/gi;
const TOKEN_RE = /\b[A-Za-z0-9_-]{20,}\b/g;
const NUMBER_RE = /\+?\d[\d\s().-]{2,}\d/g;

/** Strip anything that could identify a person or carry a secret from error text. */
export function scrubText(text) {
  if (text == null) return null;
  const out = String(text)
    .replace(URL_RE, '<url>')
    .replace(EMAIL_RE, '<email>')
    .replace(UUID_RE, '<id>')
    .replace(TOKEN_RE, '<token>')
    .replace(NUMBER_RE, (m) => (m.replace(/\D/g, '').length >= 4 ? '<n>' : m))
    .replace(/\s+/g, ' ')
    .trim();
  return out ? out.slice(0, MAX_TEXT) : null;
}

// uuid, 4+ digits, an email, a long token with a digit, or a long run with no word separators.
function isIdSegment(seg) {
  if (/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(seg)) return true;
  if (/\d{4,}/.test(seg) || seg.includes('@')) return true;
  if (seg.length >= 16 && /\d/.test(seg)) return true;
  return seg.length >= 20 && !/[-_]/.test(seg);
}

/**
 * Route path -> registry key space without ids or tokens: query and hash are
 * dropped and any id-like segment becomes ':id' (the Brain resolver matches
 * ':param' patterns against it). Next.js patterns ('/claims/[token]') pass through.
 */
export function scrubPath(path) {
  if (typeof path !== 'string' || !path.trim()) return null;
  const bare = path.trim().split('?')[0].split('#')[0];
  const segs = bare.split('/').map((seg) => {
    if (!seg || seg.startsWith('[') || seg.startsWith(':')) return seg;
    let decoded = seg;
    try { decoded = decodeURIComponent(seg); } catch { /* keep raw */ }
    return isIdSegment(decoded) ? ':id' : seg;
  });
  const out = segs.join('/').replace(/\/+$/, '') || '/';
  return out.slice(0, MAX_KEY);
}

function cleanKey(value) {
  if (typeof value !== 'string') return null;
  const v = value.trim().replace(/[^A-Za-z0-9_.:/#\-[\]]/g, '_').slice(0, MAX_KEY);
  return v || null;
}

/**
 * createUxSignals({ app, sink, isSignedIn, now, random, ...overrides })
 *   sink(event)    receives each accepted app_events-shaped event
 *   isSignedIn()   synchronous; false -> nothing is emitted
 */
export function createUxSignals(options = {}) {
  const cfg = {
    ...DEFAULTS,
    ...options,
    sampleRates: { ...DEFAULTS.sampleRates, ...(options.sampleRates || {}) },
  };
  const now = cfg.now || (() => Date.now());
  const random = cfg.random || Math.random;
  const sink = typeof cfg.sink === 'function' ? cfg.sink : () => {};
  const isSignedIn = typeof cfg.isSignedIn === 'function' ? cfg.isSignedIn : () => false;

  const lastSent = new Map();
  const stats = { emitted: 0, throttled: 0, capped: 0, unsampled: 0, signedOut: 0 };
  let cur = null; // { screen, subview, enteredAt, loaded, engagedAt, flow, completed }
  let taps = [];

  function emit(eventType, { feature = null, durationMs = null, errorMessage = null, sampleKey = eventType, extra = null } = {}) {
    if (!cur?.screen) return false;
    if (!isSignedIn()) { stats.signedOut += 1; return false; }
    const rate = cfg.sampleRates[sampleKey] ?? 1;
    if (rate < 1 && random() >= rate) { stats.unsampled += 1; return false; }
    const key = `${eventType}|${cur.screen}|${cur.subview || ''}|${feature || ''}`;
    const t = now();
    const prev = lastSent.get(key);
    if (prev != null && t - prev < cfg.throttleMs) { stats.throttled += 1; return false; }
    if (stats.emitted >= cfg.maxPerSession) { stats.capped += 1; return false; }
    lastSent.set(key, t);
    stats.emitted += 1;
    const metadata = { signal: UX_SIGNAL_VERSION, sample_rate: rate };
    if (cur.subview) metadata.subview = cur.subview;
    if (extra) Object.assign(metadata, extra);
    sink({
      event_type: eventType,
      screen: cur.screen,
      feature,
      duration_ms: durationMs == null ? null : Math.max(0, Math.round(durationMs)),
      error_message: errorMessage,
      metadata,
    });
    return true;
  }

  function leave() {
    if (!cur) return;
    const dwell = now() - cur.enteredAt;
    if (cur.engagedAt != null && !cur.completed && dwell >= cfg.minAbandonDwellMs) {
      emit('abandon', { feature: cur.flow || 'flow', durationMs: dwell, extra: { reason: 'left_before_primary_action' } });
    }
  }

  return {
    /** Route change. Closes the previous screen (abandon check) and opens the next. */
    screen(screen, { subview = null, at = null } = {}) {
      const key = cleanKey(screen);
      const sub = cleanKey(subview);
      if (cur && key === cur.screen && sub === cur.subview) return;
      leave();
      taps = [];
      cur = key ? { screen: key, subview: sub, enteredAt: at ?? now(), loaded: false, engagedAt: null, flow: null, completed: false } : null;
    },
    /** Subview changed inside the same route (CRM tab, portal mode). Not a new visit. */
    subview(subview) {
      if (cur) cur.subview = cleanKey(subview);
    },
    /** First data for the current screen arrived. Reports once per visit, only when slow. */
    dataLoaded(at = now()) {
      if (!cur || cur.loaded) return false;
      cur.loaded = true;
      const ms = at - cur.enteredAt;
      return ms > cfg.slowLoadMs ? emit('slow_load', { durationMs: ms }) : false;
    },
    /** The user started the screen's primary flow (first input). */
    flowStart(flow = 'flow') {
      if (!cur || cur.engagedAt != null) return;
      cur.engagedAt = now();
      cur.flow = cleanKey(flow) || 'flow';
    },
    /** The screen's primary action completed: time-to-complete, and no abandon on leave. */
    flowComplete(flow = null) {
      if (!cur) return false;
      const f = cleanKey(flow) || cur.flow || 'flow';
      const from = cur.engagedAt ?? cur.enteredAt;
      cur.completed = true;
      const sent = emit('feature_use', { feature: 'flow_complete', sampleKey: 'flow_complete', durationMs: now() - from, extra: { flow: f } });
      // A completed flow starts fresh (a second ticket on the same screen is a new flow).
      cur.engagedAt = null;
      cur.flow = null;
      return sent;
    },
    /** One tap/click. N taps on the same target inside a small area and window = rage. */
    tap(x, y, target = null) {
      if (!cur) return false;
      const t = now();
      const tgt = cleanKey(target);
      taps = taps.filter((p) => t - p.t <= cfg.rageWindowMs && p.target === tgt
        && Math.abs(p.x - x) <= cfg.rageRadiusPx && Math.abs(p.y - y) <= cfg.rageRadiusPx);
      taps.push({ x, y, t, target: tgt });
      if (taps.length < cfg.rageTaps) return false;
      const count = taps.length;
      taps = [];
      return emit('tap_rage', { feature: tgt || 'area', extra: { taps: count } });
    },
    /** A JS error (fatal = crash). Error text is scrubbed. */
    error(err, { fatal = false } = {}) {
      const raw = err && typeof err === 'object' ? `${err.name || 'Error'}: ${err.message || ''}` : String(err ?? 'Error');
      return emit('error', { feature: fatal ? 'crash' : 'js_error', errorMessage: scrubText(raw) });
    },
    /** App going to background / page hidden: count the current screen as left. */
    background() {
      leave();
      if (cur) { cur.engagedAt = null; cur.flow = null; }
    },
    current() {
      return cur ? { screen: cur.screen, subview: cur.subview } : null;
    },
    stats() {
      return { ...stats };
    },
  };
}

/**
 * Small batch queue for the web apps (gseat-pro reuses FeedbackService's own
 * batching). send(events) must resolve truthy on success; failures re-queue
 * once, then drop. Bounded so an offline tab never grows without limit.
 */
export function createBatcher({ send, maxBatch = 25, maxBuffered = 100 } = {}) {
  let buf = [];
  let inFlight = null;
  return {
    push(event) {
      buf.push(event);
      if (buf.length > maxBuffered) buf.splice(0, buf.length - maxBuffered);
      return buf.length >= maxBatch;
    },
    size() {
      return buf.length;
    },
    flush(opts = {}) {
      if (inFlight) return inFlight;
      if (!buf.length) return Promise.resolve(true);
      const batch = buf.splice(0, maxBatch);
      inFlight = Promise.resolve()
        .then(() => send(batch, opts))
        .then((ok) => {
          if (!ok) batch.forEach((e) => { if (!e.__retried) { e.__retried = true; buf.unshift(e); } });
          return Boolean(ok);
        }, () => {
          batch.forEach((e) => { if (!e.__retried) { e.__retried = true; buf.unshift(e); } });
          return false;
        })
        .finally(() => { inFlight = null; });
      return inFlight;
    },
  };
}
